import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Menu as MenuIcon } from 'lucide-react';

import styles from './feed.module.css';

import { Item, feedItemTransitionName } from '../home/stories/top-stories.hooks';
import { IssueButton } from '@widgets/issue-reader';
import { useFont, useNavbarIconUrl } from '@shared/lib/hooks';

import { Menu, MenuButton as MenuBtn } from '@shared/ui/menu';
import { Topbar as TopbarWidget } from '@shared/ui/topbar';

import { FeedState } from './feed.type';
import { sanitizeHtml } from '@/lib/article-renderer/core/sanitize-html';
import { T, useT } from '@shared/i18n';

export const FeedView = () => {
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const location = useLocation();
	const navigate = useNavigate();
	const { fontSize, setFontSize } = useFont();
	const [item, setItem] = useState<Item | null>(null);
	useTitle(item === null ? '' : item.title);
	useEffect(() => {
		const state = location.state as FeedState;

		if (!state) {
			navigate({
				to: '/',
				search: {
					section: 'top-stories',
				},
				replace: true,
			});

			return;
		}

		const { item, section } = state;

		if (!item) {
			navigate({
				to: '/',
				search: {
					section: section || 'top-stories',
				},
				replace: true,
			});

			return;
		}
		setItem(item);
	}, [location, navigate]);

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	return (
		<div className={styles['feed']}>
			<div className={styles['view']}>
				<Topbar toggleMenu={toggleMenu} />
				<Content item={item} fontSize={fontSize} />
			</div>
			<Menu isOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
				<MenuBtn.Settings onOpen={() => setIsMenuOpen(false)} />
				<IssueButton.About />
				<IssueButton.Font
					fontSize={fontSize}
					setFontSize={setFontSize}
				/>
			</Menu>
		</div>
	);
};

const Topbar = ({ toggleMenu }: { toggleMenu: () => void }) => {
	const t = useT();
	const location = useLocation();
	const navigate = useNavigate();
	const state = location.state as FeedState;
	const navbarIconUrl = useNavbarIconUrl();
	return (
		<TopbarWidget
			className={styles['topbar']}
			left={
				<button
					type="button"
					className={styles['back-button']}
					onClick={() => {
						navigate({
							to: '/',
							search: {
								section: state?.section || 'magazines',
							},
							replace: true,
						});
					}}
				>
					<ArrowLeft color="var(--cf-topbar-stroke)" />
				</button>
			}
			center={
				<div className={styles['title']}>
					<img src={navbarIconUrl} alt={t('common.logo')} />
				</div>
			}
			right={<MenuButton toggleMenu={toggleMenu} />}
		/>
	);
};

function MenuButton({ toggleMenu }: { toggleMenu: () => void }) {
	const t = useT();
	// Same trigger the reader uses to open this identical @shared/ui/menu
	// panel (widgets/issue-reader/responsive.tsx's own MenuButton) -- a plain
	// hamburger icon, not the blue outlined "..." pill this page had grown on
	// its own. User-reported: feed's topbar looked like a different app from
	// the one two taps away.
	return (
		<button
			type="button"
			className="tooltip down"
			data-text={t('common.menu')}
			aria-label={t('common.menu')}
			data-menu-toggle
			onClick={toggleMenu}
		>
			<MenuIcon id={'menu-btn'} color="var(--cf-app-color-ink-subtle)" />
		</button>
	);
}

const Content = ({ item, fontSize }: ContentProps) => {
	const t = useT();
	if (!item) {
		return <div className={styles['content']} />;
	}
	const { title, creator, description, pubDate, thumbnail, category, link } =
		item;
	const dateTime = DateTime.fromJSDate(pubDate);
	// Matches the same, item-identity-derived name top-stories.tsx's FeedItem
	// sets on its own thumbnail -- see feedItemTransitionName's own comment
	// for why `link` is the identifier.
	const transitionName =
		thumbnail && link ? feedItemTransitionName(link) : undefined;
	return (
		<article
			className={styles['content']}
			style={{ fontSize: fontSize ? `${fontSize}%` : undefined }}
		>
			<small className={styles['date']}>
				{dateTime.toLocaleString(DateTime.DATETIME_FULL)}
			</small>
			{thumbnail ? (
				<div className={styles['image']}>
					<img
						src={thumbnail}
						alt={t('common.cover')}
						style={
							transitionName
								? { viewTransitionName: transitionName }
								: undefined
						}
					/>
				</div>
			) : null}
			{category ? <h3>{category}</h3> : null}
			<h1 dir="auto">{title}</h1>
			{creator ? (
				<small className={styles['creator']}>
					<T
						k="template.poets.by"
						values={{ creator }}
						components={{ b: (chunks) => <b>{chunks}</b> }}
					/>
				</small>
			) : null}
			<div dangerouslySetInnerHTML={{ __html: sanitizeHtml(description ?? '') }} />
		</article>
	);
};

interface ContentProps {
	item: Item | null;
	fontSize: number;
}

/**
 * React hook that load data from navigation
 *
 */
export function useTitle(title: string | null) {
	useEffect(() => {
		if (!title) {
			return;
		}

		document.title = title;

		const metaHeader = document.querySelector('meta[name="description"]');
		if (metaHeader) {
			metaHeader.setAttribute('content', title);
		}
	}, [title]);
}

export default FeedView;
